import { useState, useRef } from "react";
import { atom, useAtom, useSetAtom } from "jotai";
import {
  Alert,
  AlertDescription,
  Grid,
  Icon,
  Spacer,
  Tag,
  Text,
} from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import { emitUserInteractionEventAtom } from "@zapier/canvas-state";
import { GradientBorder } from "../GradientBorder";
import styles from "./AiPromptBox.module.css";
import { useStreamingCompletion } from "../../hooks/useStreamingCompletion";
import { enhancedPromptAtom } from "../../atoms/enhancedPromptAtom";
import { AiPromptBuilder } from "./AiPromptBuilder";
import { fixAndParseJSON } from "../../utils";
import type { PromptPlan } from "@zapier/types";
import { useUserContext } from "../../hooks/useUserContext";

const setEnhancedPromptAtom = atom(null, (get, set, partialPrompt: string) => {
  const enhancedPrompt = get(enhancedPromptAtom);

  set(enhancedPromptAtom, enhancedPrompt + partialPrompt);
});

type AiPromptBoxProps = {
  error?: string | null;
  handleSubmit: (prompt: string) => void;
};

export function AiPromptBox({ error, handleSubmit }: AiPromptBoxProps) {
  const userContext = useUserContext();

  const [prompt, setPrompt] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [eventSent, setEventSent] = useState(false);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventAtom);
  const appendEnhancedPrompt = useSetAtom(setEnhancedPromptAtom);
  const [enhancedPrompt, setEnhancedPrompt] = useAtom(enhancedPromptAtom);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const handleInputChange = async (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const textEntered = event.target.value;
    setPrompt(textEntered);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set new height
    }

    // Send the event only on the first input change
    if (!eventSent) {
      await emitUserInteractionEvent({
        eventAction: "edit",
        interactionGoal: "Entered an AI prompt from list page",
      });
      setEventSent(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        // Allow Shift+Enter for new line
        return;
      }
      // Prevent default behavior (new line)
      event.preventDefault();

      if (!isStreaming && prompt.trim()) {
        handleGenerate();
      }
    }
  };

  const { triggerRequest } = useStreamingCompletion({
    api: "/api/zsl/canvas/prompter",
    onMessage: (msg) => {
      appendEnhancedPrompt(msg.data);
    },
    onFinish: () => {
      setIsStreaming(false);
    },
  });

  let thePlan: PromptPlan | null = null;

  if (enhancedPrompt) {
    thePlan = fixAndParseJSON(enhancedPrompt);
  }

  const handleGenerate = async () => {
    setEnhancedPrompt("");
    setIsStreaming(true);

    let apps: string[] = [];

    if (userContext.connectedApps.length > 0) {
      apps = userContext.connectedApps.map((app) => app.name);
    }

    if (userContext.recommendedApps.length > 0) {
      apps = [...apps, ...userContext.recommendedApps.map((app) => app.name)];
    }

    triggerRequest({
      prompt,
      apps: apps.length > 0 ? apps : undefined,
    });
    await emitUserInteractionEvent({
      eventAction: "click",
      interactionGoal: "Generated an enriched prompt",
    });
  };

  return (
    <GradientBorder>
      <div className={styles.promptBoxContent}>
        <div>
          <div className={styles.header}>
            <Text type="SectionHeader" color="TextDefault">
              Get a system recommendation
            </Text>
            <Tag variant="simple">
              <Grid alignItems="center" columns="auto max-content" gap={2}>
                <Icon color="TextWeakest" name="miscEvergreenAI" size={14} />
                <Text type="MinimalPrint2" color="TextWeakest">
                  AI
                </Text>
              </Grid>
            </Tag>
          </div>
          <Text type="Body1" color="TextDefault" tag="p">
            {
              "Tell us what you're building, and have Canvas create a system for you."
            }
          </Text>
          {error && (
            <>
              <Spacer height={10} />
              <Alert
                status="error"
                title="Error generating assets"
                variant="alert"
              >
                <AlertDescription>
                  {error || "Update your prompt and try again."}
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>
        <div className={styles.promptInputRow}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={prompt}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Describe your process..."
            rows={1} // Start with a single row
          />
          <div className={styles.buttonWrapper}>
            <Button
              isLoading={isStreaming}
              variant="secondary"
              onPress={handleGenerate}
            >
              Generate
            </Button>
          </div>
        </div>
        <AiPromptBuilder
          isStreaming={isStreaming}
          handleSubmit={handleSubmit}
          plan={thePlan}
        />
      </div>
    </GradientBorder>
  );
}
